import { useCallback, useEffect, useState } from "react";
import type { UsageSeries } from "api";
import { usageApi } from "api";
import { isForbidden, isUnauthenticated } from "utils/error.utils";
import type { LoadStatus } from "./useProfile";

type UseUsageSeries = {
  /** Null until the first answer arrives, and after a failed one. */
  usage: UsageSeries | null;
  status: LoadStatus;
  /** Fetch again in the background, keeping what is on screen until it answers. */
  reload: () => void;
};

/**
 * Calls per day over the last `days`, for the dashboard chart.
 *
 * A series of zeros is a legitimate answer — it is what a quiet fortnight looks
 * like, and `tracked_since` is what tells the difference between that and a
 * company the back office has not started snapshotting yet.
 */
export const useUsageSeries = (days: number, onSessionLost: () => void): UseUsageSeries => {
  const [usage, setUsage] = useState<UsageSeries | null>(null);
  const [status, setStatus] = useState<LoadStatus>("loading");

  const load = useCallback(
    (background: boolean) => {
      let cancelled = false;

      usageApi
        .daily(days)
        .then(({ data }) => {
          if (cancelled) return;
          setUsage(data.data);
          setStatus("ready");
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          if (isUnauthenticated(error)) {
            onSessionLost();
            setStatus("unauthorized");
            return;
          }
          // A refresh that fails leaves the last good chart alone: a blip is not
          // worth replacing a readable card with an error.
          if (background) return;

          setStatus(isForbidden(error) ? "unverified" : "failed");
        });

      return () => {
        cancelled = true;
      };
    },
    [days, onSessionLost]
  );

  useEffect(() => load(false), [load]);

  const reload = useCallback(() => {
    load(true);
  }, [load]);

  return { usage, status, reload };
};
